import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    FileText, Clock, Link as LinkIcon, User, Save, RotateCcw,
    CheckCircle, Loader2, AlertCircle
} from 'lucide-react';
import {
    SHOW_NOTES_TEMPLATES,
    SHOW_NOTES_TONE_OPTIONS,
    SHOW_NOTES_CTA_OPTIONS,
    SHOW_NOTES_QUICK_ACTIONS
} from '../../../ai/prompts/contentPrompts';

const SHOW_NOTES_LENGTH = 2000;
const TONE_OPTIONS = SHOW_NOTES_TONE_OPTIONS;
const CTA_OPTIONS = SHOW_NOTES_CTA_OPTIONS;
const QUICK_ACTIONS = SHOW_NOTES_QUICK_ACTIONS;
const validateShowNotes = (content) => ({ isValid: true, errors: [], warnings: [] });

/**
 * ShowNotesPreview - Dynamic show notes preview component
 * 
 * Displays a show notes preview with support for:
 * - Dynamic content injection from AI
 * - Streaming content display (typing effect)
 * - Inline content editing
 * - Drag-and-drop text content
 * - Unsaved changes tracking
 * - Save to Vault functionality
 * - Start Over functionality
 * 
 * @param {object} props
 * @param {string} props.content - The show notes content to display
 * @param {boolean} props.isStreaming - Whether content is being streamed
 * @param {string} props.guestName - Guest name for episode display
 * @param {boolean} props.isEditable - Whether content can be edited inline
 * @param {function} props.onContentChange - Callback when content changes
 * @param {boolean} props.hasUnsavedChanges - Whether there are unsaved changes
 * @param {function} props.onSave - Callback to save changes
 * @param {object} props.episode - Episode data for context
 * @param {function} props.onSaveToVault - Callback to save to vault
 * @param {function} props.onStartOver - Callback to reset and start over
 * @param {boolean} props.isSavingToVault - Whether save to vault is in progress
 */
const ShowNotesPreview = ({
    content = null,
    isStreaming = false,
    guestName = null,
    isEditable = false,
    onContentChange = null,
    hasUnsavedChanges = false,
    onSave = null,
    episode = null,
    onSaveToVault = null,
    onStartOver = null,
    isSavingToVault = false
}) => {
    // State management
    const [isEditing, setIsEditing] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [isDraggingText, setIsDraggingText] = useState(false);
    const textareaRef = useRef(null);
    const textDragCounterRef = useRef(0);

    // Default placeholder content
    const defaultContent = `# Episode Show Notes

## Summary
In this episode, we dive deep into an engaging conversation that explores **key insights** and practical takeaways for our listeners.

## Key Points
- Insight #1 from the conversation
- Insight #2 from the conversation  
- Insight #3 from the conversation

## Timestamps
00:00 - Introduction
05:00 - Main Topic
15:00 - Deep Dive
30:00 - Key Takeaways
45:00 - Closing Thoughts

## Resources
- Resource mentioned in the episode
- Related content for further reading

---
*Listen on your favorite podcast platform!*`;

    const displayContent = content || defaultContent;

    // Validation
    const validation = useMemo(() => validateShowNotes(displayContent), [displayContent]);

    // Helper function to parse inline markdown (bold, italic, code, links)
    const parseInlineMarkdown = (text) => {
        if (!text) return text;

        // Create an array of parts with their types
        const parts = [];
        let remaining = text;
        let keyIndex = 0;

        // Process the text for inline formatting
        while (remaining.length > 0) {
            // Check for bold (**text**)
            const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
            if (boldMatch) {
                parts.push(<strong key={`bold-${keyIndex++}`} className="font-bold text-gray-900">{boldMatch[1]}</strong>);
                remaining = remaining.slice(boldMatch[0].length);
                continue;
            }

            // Check for italic (*text* or _text_)
            const italicMatch = remaining.match(/^[*_]([^*_]+?)[*_]/);
            if (italicMatch) {
                parts.push(<em key={`italic-${keyIndex++}`} className="italic">{italicMatch[1]}</em>);
                remaining = remaining.slice(italicMatch[0].length);
                continue;
            }

            // Check for inline code (`code`)
            const codeMatch = remaining.match(/^`([^`]+)`/);
            if (codeMatch) {
                parts.push(
                    <code key={`code-${keyIndex++}`} className="px-1.5 py-0.5 bg-gray-100 text-indigo-600 rounded text-sm font-mono">
                        {codeMatch[1]}
                    </code>
                );
                remaining = remaining.slice(codeMatch[0].length);
                continue;
            }

            // Check for links [text](url)
            const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
            if (linkMatch) {
                parts.push(
                    <a key={`link-${keyIndex++}`} href={linkMatch[2]} className="text-indigo-600 hover:underline" target="_blank" rel="noopener noreferrer">
                        {linkMatch[1]}
                    </a>
                );
                remaining = remaining.slice(linkMatch[0].length);
                continue;
            }

            // No match found, take one character as plain text
            if (parts.length > 0 && typeof parts[parts.length - 1] === 'string') {
                parts[parts.length - 1] += remaining[0];
            } else {
                parts.push(remaining[0]);
            }
            remaining = remaining.slice(1);
        }

        // Convert string parts to proper React elements
        return parts.map((part, idx) => {
            if (typeof part === 'string') {
                return <span key={`text-${idx}`}>{part}</span>;
            }
            return part;
        });
    };

    // Format content with markdown-like rendering
    const formatContent = (text) => {
        if (!text) return null;

        // Split by lines and process each
        const lines = text.split('\n');
        const elements = [];
        let inList = false;
        let listItems = [];
        let currentListType = null;

        const flushList = () => {
            if (listItems.length > 0) {
                elements.push(
                    <ul key={`list-${elements.length}`} className="space-y-2 mb-4 ml-1">
                        {listItems}
                    </ul>
                );
                listItems = [];
                inList = false;
                currentListType = null;
            }
        };

        lines.forEach((line, index) => {
            const trimmedLine = line.trim();

            // Handle H1 headers (# )
            if (trimmedLine.startsWith('# ') && !trimmedLine.startsWith('## ')) {
                flushList();
                const headerText = trimmedLine.slice(2);
                elements.push(
                    <h1 key={index} className="text-2xl font-bold text-gray-900 mb-3 mt-4 first:mt-0">
                        {parseInlineMarkdown(headerText)}
                    </h1>
                );
                return;
            }

            // Handle H2 headers (## )
            if (trimmedLine.startsWith('## ') && !trimmedLine.startsWith('### ')) {
                flushList();
                const headerText = trimmedLine.slice(3);
                elements.push(
                    <h2 key={index} className="text-lg font-bold text-gray-900 mb-2 mt-6 flex items-center gap-2">
                        {parseInlineMarkdown(headerText)}
                    </h2>
                );
                return;
            }

            // Handle H3 headers (### )
            if (trimmedLine.startsWith('### ') && !trimmedLine.startsWith('#### ')) {
                flushList();
                const headerText = trimmedLine.slice(4);
                elements.push(
                    <h3 key={index} className="text-base font-bold text-gray-900 mb-2 mt-4">
                        {parseInlineMarkdown(headerText)}
                    </h3>
                );
                return;
            }

            // Handle bullet points (•, -, or *)
            const bulletMatch = trimmedLine.match(/^[•\-\*]\s+(.+)/);
            if (bulletMatch) {
                inList = true;
                listItems.push(
                    <li key={index} className="flex items-start gap-2.5 text-gray-700 leading-relaxed">
                        <span className="text-indigo-500 mt-1.5 text-xs">●</span>
                        <span className="flex-1">{parseInlineMarkdown(bulletMatch[1])}</span>
                    </li>
                );
                return;
            }

            // Handle numbered lists (1. 2. etc.)
            const numberedMatch = trimmedLine.match(/^\d+\.\s+(.+)/);
            if (numberedMatch) {
                inList = true;
                const numMatch = trimmedLine.match(/^(\d+)\./);
                listItems.push(
                    <li key={index} className="flex items-start gap-2.5 text-gray-700 leading-relaxed">
                        <span className="text-indigo-600 font-semibold min-w-[20px]">{numMatch[1]}.</span>
                        <span className="flex-1">{parseInlineMarkdown(numberedMatch[1])}</span>
                    </li>
                );
                return;
            }

            // Handle timestamps (MM:SS or HH:MM:SS format)
            const timestampMatch = trimmedLine.match(/^(\d{1,2}:\d{2}(?::\d{2})?)\s*[-–—]\s*(.+)$/);
            if (timestampMatch) {
                flushList();
                elements.push(
                    <div key={index} className="flex gap-3 text-sm mb-1.5 py-1">
                        <span className="font-mono text-indigo-600 font-semibold min-w-[60px] flex-shrink-0">
                            {timestampMatch[1]}
                        </span>
                        <span className="text-gray-700">{parseInlineMarkdown(timestampMatch[2])}</span>
                    </div>
                );
                return;
            }

            // Handle horizontal rules (---)
            if (trimmedLine === '---' || trimmedLine === '***' || trimmedLine === '___') {
                flushList();
                elements.push(<hr key={index} className="my-6 border-gray-200" />);
                return;
            }

            // Handle blockquotes (> text)
            if (trimmedLine.startsWith('> ')) {
                flushList();
                const quoteText = trimmedLine.slice(2);
                elements.push(
                    <blockquote key={index} className="border-l-4 border-indigo-500 pl-4 py-2 my-4 bg-indigo-50/50 rounded-r-lg">
                        <p className="text-gray-700 italic">{parseInlineMarkdown(quoteText)}</p>
                    </blockquote>
                );
                return;
            }

            // Handle links/resources (→ prefix)
            if (trimmedLine.startsWith('→ ')) {
                flushList();
                const linkText = trimmedLine.slice(2);
                elements.push(
                    <div key={index} className="text-sm text-indigo-600 hover:underline cursor-pointer mb-1.5 flex items-center gap-1.5">
                        <span className="text-indigo-400">→</span>
                        {parseInlineMarkdown(linkText)}
                    </div>
                );
                return;
            }

            // Regular paragraph text
            if (trimmedLine) {
                flushList();
                elements.push(
                    <p key={index} className="text-gray-700 leading-relaxed mb-3">
                        {parseInlineMarkdown(trimmedLine)}
                    </p>
                );
                return;
            }

            // Empty line - flush list and add spacing
            if (!trimmedLine) {
                flushList();
            }
        });

        // Flush any remaining list items
        flushList();

        return elements;
    };

    // Handle text change in editable mode
    const handleTextChange = (e) => {
        if (onContentChange) {
            onContentChange(e.target.value);
        }
    };

    // Handle click to start editing
    const handleContentClick = () => {
        if (isEditable && !isStreaming) {
            setIsEditing(true);
        }
    };

    // Handle blur to stop editing
    const handleBlur = () => {
        setIsEditing(false);
    };

    // Auto-focus textarea when editing starts
    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isEditing]);

    // Handle drag events for TEXT zone
    const handleTextDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        textDragCounterRef.current++;
        if (textDragCounterRef.current === 1) {
            if (e.dataTransfer.types.includes('application/json') || e.dataTransfer.types.includes('text/plain')) {
                setIsDraggingText(true);
            }
        }
    };

    const handleTextDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        textDragCounterRef.current--;
        if (textDragCounterRef.current === 0) {
            setIsDraggingText(false);
        }
    };

    const handleTextDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleTextDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        textDragCounterRef.current = 0;
        setIsDraggingText(false);

        // Check for vault asset drag (from VaultDrawer)
        if (e.dataTransfer.types.includes('application/json')) {
            try {
                const jsonData = e.dataTransfer.getData('application/json');
                const artifact = JSON.parse(jsonData);

                // Only handle TEXT assets from vault
                if (artifact && artifact.type === 'vault-asset' && artifact.assetType === 'text' && artifact.content) {
                    if (onContentChange) {
                        onContentChange(artifact.content);
                    }
                    return;
                }
            } catch (err) {
                console.error('Failed to parse vault asset:', err);
            }
        }

        // Handle plain text drops
        if (e.dataTransfer.types.includes('text/plain')) {
            const text = e.dataTransfer.getData('text/plain');
            if (text && onContentChange) {
                onContentChange(text);
            }
        }
    };

    return (
        <div className="flex flex-col h-full bg-white font-sans">
            {/* Action Bar - Save to Vault & Start Over */}
            {isEditable && content && (
                <div className="w-full px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {/* Word Count */}
                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                <FileText size={14} />
                                <span>{validation.stats.wordCount.toLocaleString()} words</span>
                            </div>
                            <div className="w-px h-4 bg-gray-200" />
                            {/* Save Status */}
                            {saveSuccess ? (
                                <>
                                    <CheckCircle size={14} className="text-green-500" />
                                    <span className="text-xs text-green-600 font-medium">Saved to Vault!</span>
                                </>
                            ) : hasUnsavedChanges ? (
                                <>
                                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                    <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={14} className="text-green-500" />
                                    <span className="text-xs text-green-600 font-medium">Ready to save</span>
                                </>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={onStartOver}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-all"
                            >
                                <RotateCcw size={14} />
                                Start Over
                            </button>
                            <button
                                onClick={async () => {
                                    if (onSaveToVault) {
                                        const success = await onSaveToVault();
                                        if (success) {
                                            setSaveSuccess(true);
                                            setTimeout(() => setSaveSuccess(false), 2000);
                                        }
                                    }
                                }}
                                disabled={isSavingToVault || !content}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isSavingToVault
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm'
                                    }`}
                            >
                                {isSavingToVault ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={14} />
                                        Save to Vault
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content Area */}
            <div
                className="flex-1 overflow-y-auto p-6"
                onDragEnter={handleTextDragEnter}
                onDragLeave={handleTextDragLeave}
                onDragOver={handleTextDragOver}
                onDrop={handleTextDrop}
            >
                {/* Episode Header */}
                {(episode?.episodeTitle || episode?.title || guestName) && (
                    <div className="mb-6 pb-4 border-b border-gray-100">
                        <div className="flex items-center gap-2 text-indigo-600 mb-2">
                            <FileText size={18} />
                            <span className="text-sm font-semibold uppercase tracking-wide">Episode Show Notes</span>
                        </div>
                        <h1 className="text-xl font-bold text-gray-900 mb-1">
                            {episode?.episodeTitle || episode?.title || 'Podcast Episode'}
                        </h1>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                            {episode?.episodeNumber && (
                                <span>Episode #{episode.episodeNumber}</span>
                            )}
                            {episode?.duration && (
                                <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                        <Clock size={12} />
                                        {episode.duration}
                                    </span>
                                </>
                            )}
                            {guestName && (
                                <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                        <User size={12} />
                                        {guestName}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Content Display/Edit Area */}
                {isEditing && isEditable && !isStreaming ? (
                    <textarea
                        ref={textareaRef}
                        value={displayContent}
                        onChange={handleTextChange}
                        onBlur={handleBlur}
                        className={`w-full h-full min-h-[400px] text-sm text-gray-900 leading-relaxed whitespace-pre-wrap resize-none border transition-all outline-none rounded-lg p-4 ${isDraggingText
                            ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                            : 'border-gray-200 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100'
                            }`}
                        placeholder="Write your show notes here..."
                    />
                ) : (
                    <div
                        onClick={handleContentClick}
                        className={`prose prose-indigo max-w-none transition-all rounded-lg ${isDraggingText
                            ? 'bg-indigo-50 border-2 border-dashed border-indigo-400 p-8 min-h-[200px] flex items-center justify-center'
                            : ''
                            } ${isEditable && !isStreaming ? 'cursor-text hover:bg-gray-50' : ''}`}
                    >
                        {isDraggingText ? (
                            <div className="text-center">
                                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-indigo-100 flex items-center justify-center">
                                    <FileText size={24} className="text-indigo-600" />
                                </div>
                                <span className="text-indigo-600 font-semibold text-base">
                                    Drop text content here
                                </span>
                                <p className="text-indigo-400 text-xs mt-1">
                                    Release to insert text
                                </p>
                            </div>
                        ) : (
                            <div className="whitespace-pre-wrap">
                                {formatContent(displayContent)}
                                {isStreaming && (
                                    <span className="inline-block w-1.5 h-4 bg-indigo-500 animate-pulse ml-0.5" />
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Validation Warnings */}
                {validation.warnings.length > 0 && isEditable && content && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                            <AlertCircle size={16} className="text-amber-600 mt-0.5" />
                            <div>
                                <p className="text-xs font-semibold text-amber-700 mb-1">Suggestions:</p>
                                <ul className="text-xs text-amber-600 space-y-1">
                                    {validation.warnings.map((warning, idx) => (
                                        <li key={idx}>• {warning}</li>
                                    ))}
                                    {validation.suggestions.map((suggestion, idx) => (
                                        <li key={`sugg-${idx}`}>• {suggestion}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ========================================
// EXPORTS
// ========================================

export { ShowNotesPreview };
// Re-export constants (now defined locally above)
export {
    SHOW_NOTES_LENGTH,
    SHOW_NOTES_TEMPLATES,
    TONE_OPTIONS,
    CTA_OPTIONS,
    QUICK_ACTIONS
};
export default ShowNotesPreview;
